import { useCallback, useEffect, useState } from "react";
import { Table } from "../utilities/types";
import tableService from "../services/tableService";
import { toast } from "react-toastify";
import { useOnlineStatus } from "./useOnlineStatus";

export const useTables = () => {
  const [tables, setTables] = useState<Table[]>(() => {
    return tableService.getTablesFromCache();
  });

  const [isLoading, setIsLoading] = useState(false);
  const isOnline = useOnlineStatus();

  const fetchTables = useCallback(async () => {
    // ✅ 2. Only try to fetch from the API if we are online
    if (!isOnline) {
      toast.info("You are offline. Showing cached table data.");
      return;
    }

    setIsLoading(true);
    try {
      const freshTables = await tableService.fetchTablesFromApi();
      // Update the state with fresh data
      setTables(freshTables);
      // ✅ 3. Save the fresh data to the cache for next time
      tableService.saveTablesToCache(freshTables);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to fetch tables",
      );
      // If fetching fails, the user still sees the old cached data
    } finally {
      setIsLoading(false);
    }
  }, [isOnline]); // Re-run fetch logic when online status changes

  // Fetch tables on initial load and whenever connectivity is restored
  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  return {
    tables,
    isLoading,
    fetchTables, // You can still expose this for manual refresh
  };
};
